import { getCurrency } from 'utils/utils';
import { Hotel, HotelRoom } from 'ts/hotel/interfaces/HotelResponse';
import { RoomType, RoomTypeGrp, RoomTypeOption, SupplierItem } from 'ts/hotel/classes/FilterModel';
import { Price } from 'ts/hotel/interfaces/HotelDetails';

export interface BoardType {
  code: string;
  name: string;
}

export interface LocationValue {
  lat: number;
  lng: number;
  name: string;
  km?: number;
  isHotel?: boolean;
}

export class FilterHotelModel {
  name: string;
  room: string;
  price: {
    min: number;
    max: number;
    currencySign: string;
    value: [number, number];
  };
  stars: {
    options: number[];
    value: number[];
  };
  location: {
    value: LocationValue;
    isHotel?: boolean;
    km?: number;
  };
  additional: {
    options: BoardType[];
    value: string[];
    freeCxl: boolean;
    withMeal: boolean;
    preffered: boolean;
    ada: boolean;
  };
  roomTypes: RoomTypeGrp[];
  roomTypesArr?: string[];
  suppliers: SupplierItem[];
  meal: any;

  constructor(
    hotels: Hotel[],
    selectedRoomPrice: number,
    numberOfPax: number,
    currency: string,
    isFreeCxlDefault: boolean
  ) {
    this.name = '';
    this.room = '';
    this.roomTypes = [];
    this.suppliers = [];

    const prices = hotels.reduce((acc: number[], hotel) => {
      return [...acc, ...hotel.roomClasses.map((room) => room.price[currency as keyof Price])];
    }, []);

    const minPriceUniq = prices.length
      ? prices.reduce((min, price) => Math.min(min, price), prices[0])
      : 0;
    const maxPriceUniq = prices.length
      ? prices.reduce((max, price) => Math.max(max, price), prices[0]) + 1
      : 1;
    const minPrice = Math.floor(minPriceUniq / numberOfPax - selectedRoomPrice);
    const maxPrice = Math.ceil(maxPriceUniq / numberOfPax - selectedRoomPrice);
    this.price = {
      min: minPrice,
      max: maxPrice,
      value: [minPrice, maxPrice],
      currencySign: getCurrency(currency),
    };

    const stars: number[] = hotels
      .map((hotel) => Math.floor(hotel.item.star))
      .filter((star, index, arr) => arr.indexOf(star) === index)
      .sort((a, b) => b - a);
    this.stars = {
      options: stars,
      value: [],
    };
    this.meal = {
      value: [],
      active: [],
    };

    this.location = {
      value: {
        lat: 0,
        lng: 0,
        name: '',
      },
    };

    const availableBoardTypes = hotels
      .reduce((acc: BoardType[], hotel) => {
        var boardTypes: BoardType[] = hotel.roomClasses.map((room) => ({
          code: room.board.basis.code,
          name: room.board.basis.desc,
        }));
        return [...boardTypes, ...acc];
      }, [])
      .filter((board, index, arr) => arr.findIndex((b) => b.code === board.code) === index);

    const isSomeCxlTrue = hotels.some((htl) =>
      htl.roomClasses.some((rc) => new Date(rc.cxl.limitDate) > new Date())
    );

    this.additional = {
      options: availableBoardTypes,
      value: [],
      freeCxl: isFreeCxlDefault && isSomeCxlTrue,
      withMeal: false,
      preffered: false,
      ada: false,
    };

    this.suppliers = [];
    this.roomTypesArr = [];

    this.roomTypes = Object.keys(RoomType).map((key) => ({
      title: key,
      options: [],
    }));

    this.roomTypes.forEach((type) => {
      const optionsMap = new Map<string, RoomTypeOption>();

      hotels.forEach((hotel) => {
        hotel.roomClasses.forEach((rClass) => {
          const valData = rClass.hotelRooms[0][type.title as keyof HotelRoom];
          if (!valData) return;

          valData.split(', ').forEach((val: any) => {
            if (!val) return;

            const existingOption = optionsMap.get(val);

            if (existingOption) {
              existingOption.count++;
              existingOption.price = Math.min(
                existingOption.price,
                rClass.price[currency as keyof Price]
              );
            } else {
              optionsMap.set(val, {
                name: val,
                checked: false,
                count: 1,
                price: rClass.price[currency as keyof Price],
                visible: true,
              });
            }
          });
        });
      });

      type.options = Array.from(optionsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    });
  }
}
